/**
 * Flow Node Validator
 * Validates Decision (Logical Conditions), Loop, and Delay nodes
 */

import { WorkflowNode } from '@store/workflowStore'
import { WorkflowNodeType, WorkflowConditionType } from '@features/adventures/types/workflow.types'
import {
  ValidationIssue,
  ValidationSeverity,
  ValidationCategory,
  ValidationErrorCodes,
} from '@features/adventures/types/validation.types'
import { createIssue } from './helpers'

/**
 * Validate a Decision (Logical Conditions) node
 */
function validateDecision(node: WorkflowNode): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const conditions = node.data.conditions || []

  // Must have at least one condition
  if (conditions.length === 0) {
    issues.push(createIssue({
      nodeId: node.id,
      nodeLabel: node.data.label,
      nodeType: node.data.nodeType,
      severity: ValidationSeverity.Error,
      category: ValidationCategory.Configuration,
      code: ValidationErrorCodes.MISSING_CONDITION,
      message: 'Logical Conditions node must have at least one condition',
    }))
    return issues
  }

  // Validate each condition's configuration
  for (const condition of conditions) {
    const config = condition.config || {}

    switch (condition.conditionType) {
      case WorkflowConditionType.Geofence:
        // Geofence requires location - check based on geofence type
        // Circle geofence uses centerLatitude/centerLongitude
        // Polygon geofence uses polygonCoordinates array
        const geofenceType = config.geofenceType || 'circle'
        const hasCircleLocation = config.centerLatitude && config.centerLongitude
        const hasPolygonLocation = Array.isArray(config.polygonCoordinates) && config.polygonCoordinates.length >= 3
        const hasLocation = geofenceType === 'polygon' ? hasPolygonLocation : hasCircleLocation

        if (!hasLocation) {
          issues.push(createIssue({
            nodeId: node.id,
            nodeLabel: node.data.label,
            nodeType: node.data.nodeType,
            severity: ValidationSeverity.Error,
            category: ValidationCategory.Configuration,
            code: ValidationErrorCodes.MISSING_GEOFENCE_LOCATION,
            message: geofenceType === 'polygon'
              ? 'Geofence condition requires a polygon to be drawn'
              : 'Geofence condition requires a location to be set',
            details: condition.branchKey ? `Branch: ${condition.branchKey}` : undefined,
          }))
        }
        break

      case WorkflowConditionType.AdventureCompleted:
        // Adventure Completed requires adventureId
        if (!config.adventureId) {
          issues.push(createIssue({
            nodeId: node.id,
            nodeLabel: node.data.label,
            nodeType: node.data.nodeType,
            severity: ValidationSeverity.Error,
            category: ValidationCategory.Configuration,
            code: ValidationErrorCodes.MISSING_ADVENTURE,
            message: 'Adventure Completed condition requires an adventure to be selected',
            details: condition.branchKey ? `Branch: ${condition.branchKey}` : undefined,
          }))
        }
        break

      case WorkflowConditionType.AdventureCollectionCompletion:
        // Collection Completion requires collectionId
        if (!config.collectionId) {
          issues.push(createIssue({
            nodeId: node.id,
            nodeLabel: node.data.label,
            nodeType: node.data.nodeType,
            severity: ValidationSeverity.Error,
            category: ValidationCategory.Configuration,
            code: ValidationErrorCodes.MISSING_COLLECTION,
            message: 'Collection Completion condition requires a collection to be selected',
            details: condition.branchKey ? `Branch: ${condition.branchKey}` : undefined,
          }))
        }
        break

      case WorkflowConditionType.GroupMembership:
        // Group Membership requires groupId
        if (!config.groupId) {
          issues.push(createIssue({
            nodeId: node.id,
            nodeLabel: node.data.label,
            nodeType: node.data.nodeType,
            severity: ValidationSeverity.Error,
            category: ValidationCategory.Configuration,
            code: ValidationErrorCodes.MISSING_GROUP,
            message: 'Group Membership condition requires a group to be selected',
            details: condition.branchKey ? `Branch: ${condition.branchKey}` : undefined,
          }))
        }
        break

      // Other condition types (weather, time, etc.) can have defaults
      // so we don't require additional validation for them
    }
  }

  return issues
}

/**
 * Validate a Loop node
 * Note: Handle connectivity is validated by connectivity.validator.ts
 */
function validateLoop(node: WorkflowNode): ValidationIssue[] {
  // Loop nodes mainly need their handles connected, which is covered by connectivity validator
  // No additional configuration validation needed
  return []
}

/**
 * Validate a Delay node
 * Note: Handle connectivity is validated by connectivity.validator.ts
 */
function validateDelay(node: WorkflowNode): ValidationIssue[] {
  // Delay nodes mainly need their handles connected, which is covered by connectivity validator
  // The delay config (duration, unit) has defaults so no validation needed
  return []
}

/**
 * Validate a Route node
 */
function validateRoute(node: WorkflowNode, allNodes: WorkflowNode[]): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const config = node.data.routeNodeConfig

  // Must have source location selected
  if (!config?.sourceNodeId) {
    issues.push(createIssue({
      nodeId: node.id,
      nodeLabel: node.data.label,
      nodeType: node.data.nodeType,
      severity: ValidationSeverity.Error,
      category: ValidationCategory.Configuration,
      code: ValidationErrorCodes.MISSING_ROUTE_SOURCE,
      message: 'Route must have a source location selected',
    }))
  } else {
    // Validate that source node exists and is a PoiLocation node
    const sourceNode = allNodes.find(n => n.id === config.sourceNodeId)
    if (!sourceNode) {
      issues.push(createIssue({
        nodeId: node.id,
        nodeLabel: node.data.label,
        nodeType: node.data.nodeType,
        severity: ValidationSeverity.Error,
        category: ValidationCategory.Configuration,
        code: ValidationErrorCodes.INVALID_ROUTE_SOURCE,
        message: 'Source location node no longer exists',
      }))
    } else if (sourceNode.data.nodeType !== WorkflowNodeType.PoiLocation) {
      issues.push(createIssue({
        nodeId: node.id,
        nodeLabel: node.data.label,
        nodeType: node.data.nodeType,
        severity: ValidationSeverity.Error,
        category: ValidationCategory.Configuration,
        code: ValidationErrorCodes.INVALID_ROUTE_SOURCE,
        message: 'Source must be a POI Location node',
      }))
    }
  }

  // Must have destination location selected
  if (!config?.destinationNodeId) {
    issues.push(createIssue({
      nodeId: node.id,
      nodeLabel: node.data.label,
      nodeType: node.data.nodeType,
      severity: ValidationSeverity.Error,
      category: ValidationCategory.Configuration,
      code: ValidationErrorCodes.MISSING_ROUTE_DESTINATION,
      message: 'Route must have a destination location selected',
    }))
  } else {
    // Validate that destination node exists and is a PoiLocation node
    const destNode = allNodes.find(n => n.id === config.destinationNodeId)
    if (!destNode) {
      issues.push(createIssue({
        nodeId: node.id,
        nodeLabel: node.data.label,
        nodeType: node.data.nodeType,
        severity: ValidationSeverity.Error,
        category: ValidationCategory.Configuration,
        code: ValidationErrorCodes.INVALID_ROUTE_DESTINATION,
        message: 'Destination location node no longer exists',
      }))
    } else if (destNode.data.nodeType !== WorkflowNodeType.PoiLocation) {
      issues.push(createIssue({
        nodeId: node.id,
        nodeLabel: node.data.label,
        nodeType: node.data.nodeType,
        severity: ValidationSeverity.Error,
        category: ValidationCategory.Configuration,
        code: ValidationErrorCodes.INVALID_ROUTE_DESTINATION,
        message: 'Destination must be a POI Location node',
      }))
    }
  }

  // Warning if no route points defined
  if (!config?.routePoints || config.routePoints.length === 0) {
    issues.push(createIssue({
      nodeId: node.id,
      nodeLabel: node.data.label,
      nodeType: node.data.nodeType,
      severity: ValidationSeverity.Warning,
      category: ValidationCategory.Configuration,
      code: ValidationErrorCodes.MISSING_ROUTE_POINTS,
      message: 'Route has no path defined - generate or import route data',
    }))
  }

  return issues
}

/**
 * Validates all flow control nodes in the workflow
 */
export function validateFlowNodes(nodes: WorkflowNode[]): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  for (const node of nodes) {
    switch (node.data.nodeType) {
      case WorkflowNodeType.Decision:
        issues.push(...validateDecision(node))
        break
      case WorkflowNodeType.Loop:
        issues.push(...validateLoop(node))
        break
      case WorkflowNodeType.Delay:
        issues.push(...validateDelay(node))
        break
      case WorkflowNodeType.Route:
        issues.push(...validateRoute(node, nodes))
        break
      case WorkflowNodeType.TimerControl:
        // No config required — pausesTimer defaults to true
        break
    }
  }

  return issues
}
